import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import draftImg from '../../resources/images/draft_vid.png';
import Header from '../../Components/Header/Header';
import { useAuthContext } from '../../hooks/UseAuthContext';
import Loader from '../../Components/Loader/Loader';
import api from '../../api';
import { toast } from 'react-toastify';
// import './completedvids.css';

const VIDEOS_PER_PAGE = 12;

function ProcessingDrafts() {
  const [videos, setVideos] = useState([]);
  const [dreamfaceJobs, setDreamfaceJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const { user } = useAuthContext();

  // Fetch users for filtering
  useEffect(() => {
    api.get('/users', {
      headers: { Authorization: `Bearer ${user.token}` }
    })
      .then(response => setUsers(response.data))
      .catch(error => console.error('Error fetching users', error));
  }, [user?.token]);

  const loadDrafts = useCallback(() => {
    const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
    return Promise.all([
      api.get('/video-drafts').catch(() => ({ data: [] })),
      api.get('/dreamface/jobs', { headers }).catch(() => ({ data: { jobs: [] } })),
    ]).then(([draftRes, dfRes]) => {
      const processingOnly = (draftRes.data || []).filter(
        video => video.status === 'processing' || video.status === 'awaiting_dreamface'
      );
      setVideos(processingOnly);
      const activeDf = (dfRes.data?.jobs || []).filter(
        j => j.status === 'processing' || j.status === 'awaiting_dreamface'
      );
      setDreamfaceJobs(activeDf);
    }).catch(error => {
      console.error('Error fetching processing drafts:', error);
    }).finally(() => setLoading(false));
  }, [user?.token]);

  useEffect(() => {
    loadDrafts();
    const timer = setInterval(loadDrafts, 20000);
    return () => clearInterval(timer);
  }, [loadDrafts]);

  const handleNavigate = (id) => {
    toast.error('Video is still processing. Please check back later.');
  };

  const handleDreamfaceOpen = (jobId) => {
    navigate(`/dreamface/${jobId}`);
  };

  // Filter and sort videos
  const filteredVideos = useMemo(() => {
    const filtered = selectedUserEmail
      ? videos.filter(video => video.user_id === selectedUserEmail)
      : videos;

    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [videos, selectedUserEmail]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredVideos.length / VIDEOS_PER_PAGE);
  const startIndex = (currentPage - 1) * VIDEOS_PER_PAGE;
  const endIndex = startIndex + VIDEOS_PER_PAGE;
  const currentVideos = filteredVideos.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedUserEmail]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="completed-vids-container">
      <Header />

      <div className="completed-vids">
        {/* Header with Filter */}
        <div className="complete-vid-header">
          <h2>⏳ Videos in Progress</h2>
          <h3>Filter By User</h3>
          <select
            className="select-user-video"
            value={selectedUserEmail}
            onChange={(e) => setSelectedUserEmail(e.target.value)}
          >
            <option value="">— All Users —</option>
            {users?.map((user, index) => (
              <option key={index} value={user.email}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
        </div>

        {/* DreamFace / Talking Heads jobs */}
        {dreamfaceJobs.length > 0 && (
          <>
            <h3 className="th-drafts-subhead">Talking Heads jobs</h3>
            <div className="videos">
              {dreamfaceJobs.map((job) => (
                <div
                  key={job._id}
                  className="video-element processing-element dreamface-draft"
                  onClick={() => handleDreamfaceOpen(job._id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleDreamfaceOpen(job._id)}
                >
                  <div className="processing-overlay">
                    <div className="processing-spinner" />
                  </div>
                  <img src={draftImg} alt="Talking heads job" />
                  <h3>{job.title || 'Talking Heads video'}</h3>
                  <p className="dreamface-draft-status">{job.status.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Video Grid */}
        {currentVideos.length > 0 ? (
          <>
            <div className="videos">
              {currentVideos.map((video) => (
                <div
                  key={video._id}
                  className="video-element processing-element"
                  onClick={() => handleNavigate(video._id)}
                >
                  <div className="processing-overlay">
                    <div className="processing-spinner" />
                  </div>
                  <img src={draftImg} alt="Processing draft thumbnail" />
                  <h3>{video.title || 'Processing…'}</h3>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {startIndex + 1}–{Math.min(endIndex, filteredVideos.length)} of {filteredVideos.length}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  ← Prev
                </button>

                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span key={`dots-${index}`} className="page-dots">
                      ⋯
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={currentPage === page ? 'active' : ''}
                      aria-label={`Page ${page}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : dreamfaceJobs.length === 0 ? (
          <div className="empty-state">
            <p>No videos are currently processing.</p>
          </div>
        ) : null}
      </div>

      {loading && <Loader />}
    </div>
  );
}

export default ProcessingDrafts;